package com.dynamicruntime.common.home

import com.dynamicruntime.common.uiblock.UiActionDef

// Home/shell constants that the *frontend* (Kotlin/JS) shares with the backend: the UI-config endpoint path,
// the layout feature flags, the response field names, the schema type name, and the fragment/document ids.
// They live in the KMP kernel (not base:common) so the transpiled frontend references the same strings the
// backend serves, instead of re-hardcoding them -- the same arrangement as the auth constants (issue #70).

/** Home/shell endpoint paths (as the frontend calls them, before the API context root is prepended). */
@Suppress("ConstPropertyName")
object HEP {
    const val homeUiConfig = "/home/ui/config"
}

/**
 * Layout feature flags for the home/shell widget-group: the three ways the home page can present its document
 * links ([HFLD.links]). Independent toggles, not a single mode -- a deployment may enable any combination, or
 * none (leaving just the page copy). They are about the *links*; the application's own top app bar (brand and
 * account menu) is separate chrome and is not governed by these.
 */
@Suppress("ConstPropertyName")
object HFEAT {
    /** Whether the links appear as a horizontal menu bar above the content. */
    const val topBar = "topBar"

    /** Whether the links appear in a left nav bar beside the content. */
    const val leftBar = "leftBar"

    /** Whether the links are listed inline in the page body. */
    const val inlineLinks = "inlineLinks"

    /**
     * Whether this caller may administer other users -- today "has the admin role", later something narrower
     * (e.g., may edit only users in their own client). It is expressed as a **capability of the caller**, not
     * as their role, precisely so that refinement changes what the backend computes here and nothing else: the
     * frontend already asks "may I?" rather than "am I an admin?"
     */
    const val canManageUsers = "canManageUsers"

    /**
     * Whether this caller administers **across clients** (holds `allClients`, issue #668) -- the signal a
     * cross-client surface keys on, like the forms list's Client column and its filter-by-client. A capability
     * of the caller, distinct from [canManageUsers], which a client-scoped administrator also has.
     */
    const val canSeeAllClients = "canSeeAllClients"

    /**
     * Whether this caller administers a **client** -- their own, or every one (issue #1091) -- which is whether
     * their listings reach beyond their own rows. Distinct from [canManageUsers], true for an administrator in
     * `public` who reaches only their own users: a surface named for whose rows it shows ("Forms" against "My
     * forms") keys on this. The home menu asks the same rule as a cfact, so the menu and the page agree.
     */
    const val administersClient = "administersClient"

    /**
     * Whether the caller is an **editor** of its client (issue #1432): sees every user of it, administering none, so the
     * Users page is offered read-only -- over the `clientDirectory` reads, with none of the controls that change a user.
     */
    const val browsesUsers = "browsesUsers"

    /**
     * Whether the caller's reads reach **every form of a client** (issue #1432): an administrator of one, or an editor.
     * What names the forms list "Forms" rather than "My forms" and shows whose form each is, as the menu's
     * `seesClientForms` cfact does.
     */
    const val seesClientForms = "seesClientForms"

    /**
     * Whether the caller may open their own client's **sandbox** (issue #931): an administrator of a client whose
     * definition asks for one, outside it -- what offers the shell's "Open sandbox" action. Asked by the rule the
     * open endpoint enforces (`openSandbox`), so the offer and the gate agree.
     */
    const val canOpenSandbox = "canOpenSandbox"

    /**
     * Whether the caller's client declares a **survey** workflow (issue #695) -- the signal the forms list keys
     * its survey-status filter on. A fact about the client's configuration rather than about the rows on
     * screen: the status column appears when some row carries survey state, but a control that vanished the
     * moment a filter matched nothing could never be cleared.
     */
    const val hasSurvey = "hasSurvey"
}

/** Home UI-config response field (JSON key) names, under the shared `state` envelope entry. */
@Suppress("ConstPropertyName")
object HFLD {
    /** `state.links`: the navigable documents, in display order. */
    const val links = "links"

    /**
     * `state.menu`: the app-bar menu items for **this caller**, in display order. The backend decides what a
     * given user may see (see [HMENU]); the frontend renders the list it is handed and adds nothing of its own.
     */
    const val menu = "menu"

    /** `state.userInfo`: who the caller is (the anonymous profile when signed out). */
    const val userInfo = "userInfo"

    /**
     * `state.users`: the users the caller's identity may act as (issue #749), as `UserChoice`s -- registered
     * and enabled ones only, the current one marked. Empty when signed out, and a list of one for a person with
     * a single user, which is what tells the bar there is nothing to switch to.
     */
    const val users = "users"

    /** A link's stable id (used to address it in the frontend's URL). */
    const val id = "id"

    /** A link's display label. */
    const val label = "label"

    /** The Markdown document a link opens, fetched at `/<staticRoot>/<appId>/doc/<docId:buildId>`. */
    const val docId = "docId"

    /** The document's cache-busting content hash. */
    const val buildId = "buildId"

    /**
     * `state.links[].sourcePath`: the document's repo-relative source path (e.g. `"README.md"`). The frontend
     * resolves the document's interior relative links against it, and builds the repo->in-app-document map from
     * every link's pair of this and [id] (issue #492).
     */
    const val sourcePath = "sourcePath"

    /** A link's one-line description, shown beside it in the Documents listing (issue #554). */
    const val description = "description"

    /**
     * `state.links[].listed`: whether the Documents listing offers the document (issue #1136). An unlisted one is a
     * page of a suite, reached from its entry page; it is still sent, so a link to it resolves.
     */
    const val listed = "listed"

    /**
     * `state.sourceRepoBase`: the source repository's blob base (`.../blob/<branch>`), or absent when the
     * deployment turned it off. Interior links to files that are *not* served as in-app documents are rewritten
     * under it (issue #492); without it they are drawn as their text (issue #1380).
     */
    const val sourceRepoBase = "sourceRepoBase"

    /**
     * `state.sandboxOf`: when the caller is in a client's sandbox (issue #931), that client's id -- what the shell's
     * marker names and its way back leads to; absent everywhere else.
     */
    const val sandboxOf = "sandboxOf"

    /** `state.sandboxOfName`: beside [sandboxOf], the client's display name, for the marker's words. */
    const val sandboxOfName = "sandboxOfName"

    /**
     * `state.timeZone`: the IANA time zone the webapp shows the caller's moments in (issue #1115) -- their client's,
     * else the deployment's display default; absent when neither is set, for the deployment's day start.
     */
    const val timeZone = "timeZone"

    /**
     * `state.timeZoneLabel`: beside [timeZone], the zone's generic short name ("CT"), what a moment shown in it is labeled
     * with (issue #1321); absent for a zone named only by its offset, whose moments are labeled by that ("GMT-3").
     */
    const val timeZoneLabel = "timeZoneLabel"

    /**
     * `state.sharedDemo`: true when the caller's client is a demo client visitors join (issue #1248), or its sandbox, on
     * a deployment that offers joining -- so every page carries the standing note that this is a shared space; absent otherwise.
     */
    const val sharedDemo = "sharedDemo"

    /**
     * `state.selfServeClient`: for a caller signed in to `public`, the self-serve client their person has created
     * (issue #1228) -- a person creates one -- so the home page offers to open it rather than to create another
     * without trying the create; absent when they have made none.
     */
    const val selfServeClient = "selfServeClient"

    /**
     * `state.selfServeClientAbsent`: beside [selfServeClient], true when this node does not carry that client -- the
     * platform retired it, or it runs elsewhere -- so the page says so rather than offering to open it; absent otherwise.
     */
    const val selfServeClientAbsent = "selfServeClientAbsent"

    /**
     * `state.joinOffered`: true for a caller signed in to `public` on a deployment that offers joining a demo client
     * (issue #1248), so the public home page (issue #1324) asks which demos there are; absent otherwise, and the page
     * draws no demos section rather than asking and being refused.
     */
    const val joinOffered = "joinOffered"

    /**
     * `state.sandboxStranded`: beside [sandboxOf], true when the caller's sandbox user acts with no roles (issue
     * #1197), so the shell says access has ended instead of offering doors that will not open; absent otherwise.
     * Whatever the reason: the user it was opened from is disabled or no longer stands for the parent (lost the
     * capability that let it in from elsewhere, say), nobody in the parent stands behind a sandbox user holding
     * `admin`, or the sandbox user was itself disabled.
     */
    const val sandboxStranded = "sandboxStranded"

    /**
     * `state.webAssets`: the caller's client's own web assets (issue #1130), a slot (`WAS.favicon`,
     * `WAS.brandMark`) to its image by name and content (`favicon:<hash>`). Only the slots the client has set are
     * in it: an absent slot is the deployment's own image, exactly as before. The frontend fetches each at
     * `WAS.staticPath`, and re-reads this with the rest of the shell config, so an upload shows without a reload.
     */
    const val webAssets = "webAssets"

    /**
     * `state.fileRefs`: the file refs the caller's client is served (issue #1186) -- the sandbox's, drafts included,
     * for someone in it -- each id to its file by name and content (`logo:<hash>`). What copy naming an image
     * `ref:logo` is drawn from, at `FREF.staticPath`: the name changes when the file does, so a browser keeps an
     * image for good and a replaced one is a new address. A ref whose file is not there is left out, and copy
     * naming it draws its alt text. Re-read with the rest of the shell config, as [webAssets] is.
     */
    const val fileRefs = "fileRefs"

    /**
     * In the shell config's state (issue #1339): each image a component ships, by its rooted name (`sample:diagram`),
     * to its image by name and content (`sample:diagram:<hash>`) -- what copy's `img:sample:diagram` is drawn from, at
     * `IMG.staticPath`. The same for every caller.
     */
    const val images = "images"

    /**
     * In the shell config's state (issue #1463): each image a component ships that is kept in the repository, by its
     * path there (`docs/images/walk-part1.svg`) to its rooted name, a key of [images]. A document's link to that path is
     * drawn as the image, so one plain Markdown image shows both on GitHub and in the app. The same for every caller.
     */
    const val imagePaths = "imagePaths"
}

/** Home schema type names (the backend's output type refs; also useful to the frontend). */
@Suppress("ConstPropertyName")
object HTYPE {
    const val homeUiConfig = "HomeUiConfig"

    /** One navigable document in `state.links`. */
    const val homeLink = "HomeLink"

    /** One app-bar menu item in `state.menu`. */
    const val menuItem = "MenuItem"
}

/**
 * Menu item ids and the frontend page ids they open.
 *
 * The **id** is the contract: it identifies an item across backend and frontend regardless of its label, so
 * the frontend can style or place one specially without matching on display text. Which items a given caller
 * receives is decided entirely by the backend -- an item absent from `state.menu` is one this user may not
 * have, so the frontend never re-derives visibility from roles it happens to know.
 */
@Suppress("ConstPropertyName")
object HMENU {
    /** The UiBlock the menu is registered as (issue #458); its items merge by [HFLD.id]. */
    const val block = "homeMenu"

    // Item ids.
    /** Reference group parent: the pages read to learn something, visited far less than the daily ones. */
    const val reference = "reference"
    const val catalog = "catalog"
    /** The documents listing page (issue #554). */
    const val docs = "docs"
    const val users = "users"
    /** The clients listing (issue #905): the clients an administrator oversees, each with its status and counts. */
    const val clients = "clients"
    /**
     * The same page for an administrator of **one** client (issue #1082), where it opens that client rather than a
     * listing: an item of its own so that its label can say so. Shown instead of [clients], never beside it.
     */
    const val myClient = "myClient"
    /** The customers a deployment administrator oversees (issue #1114), each opening to its clients. */
    const val customers = "customers"
    /**
     * The same page for an administrator of a customer's **root** client (issue #1114), where it opens their own
     * customer rather than a listing: an item of its own so that its label can say so.
     */
    const val myCustomer = "myCustomer"
    /** The named reports an administrator may run over their client's forms (issue #981). */
    const val reports = "reports"
    /** The API keys of the users an administrator administers (issue #1402). */
    const val apiKeys = "apiKeys"
    /** The simulations a test instance offers, to provision a canned scenario and sign in to it (issue #997). */
    const val simulations = "simulations"
    const val envReference = "envReference"
    /** Operator boot-checks page (issue #540). */
    const val bootChecks = "bootChecks"

    /** Operator group parent, its overview child, and its remaining pages (issue #540). */
    const val operator = "operator"
    const val operatorOverview = "operatorOverview"
    const val systemInfo = "systemInfo"
    const val dbTables = "dbTables"
    const val fragmentsCheck = "fragmentsCheck"
    const val cacheState = "cacheState"
    /** The operator's file-store browser (issue #1297). */
    const val fileStore = "fileStore"
    const val cfactReference = "cfactReference"
    // No `newForm` item id: creating a form is reached from the "My forms" list, not a menu entry (issue #417).
    // The `pageNewForm` route below still exists -- the list's "New form" button navigates to it.
    const val forms = "forms"

    /**
     * The same forms listing for a caller who administers a client (issue #1091), whose listing is every form in
     * it and not only their own: an item of its own so that its label can say "Forms". Shown instead of [forms],
     * never beside it -- and under its own id, so a client's overlay of [forms] keeps meaning what its ordinary
     * users see.
     */
    const val clientForms = "clientForms"

    /**
     * The workflow pages (issue #792). **Off by default** -- in the base menu under the never-true condition -- and
     * turned on by a client whose users work in normal workflows, with an overlay that gives it a real condition.
     */
    const val workflows = "workflows"
    /** Account group parent (issue #540): identity/session items nest under it. */
    const val account = "account"
    const val profile = "profile"
    const val login = "login"
    const val register = "register"
    const val logout = "logout"
    /**
     * Under Account, for a caller with test access (issue #1183): become the deployment admin in one click, or any
     * user through the `becomeUser` fixture's own form.
     */
    const val becomeDeploymentAdmin = "becomeDeploymentAdmin"
    const val becomeUser = "becomeUser"
    // Debug (issue #517), offered only in an env-authed session. `debugEnable` (a top-level call) turns debug
    // on; once on, `debug` is a parent item whose children -- the debug pages and `debugOff` -- drill down
    // under it via [UIB.parentId]. `debugEnable` and `debug` are mutually exclusive by cfact.
    const val debugEnable = "debugEnable"
    const val debug = "debug"
    const val debugPages = "debugPages"
    const val debugOff = "debugOff"

    // Frontend page ids, carried in a menu item's `action` as a string; the frontend maps them onto its
    // own routing (issue #483).
    const val pageCatalog = "catalog"
    const val pageDocs = "docs"
    const val pageUsers = "users"
    const val pageEnv = "env"
    const val pageBootChecks = "bootChecks"
    const val pageSystemInfo = "systemInfo"
    const val pageDbTables = "dbTables"
    const val pageFragmentsCheck = "fragmentsCheck"
    const val pageCacheState = "cacheState"
    const val pageFileStore = "fileStore"
    /** The Operator index/landing page (issue #540), mirroring the Debug index. */
    const val pageOperator = "operator"
    const val pageCfacts = "cfacts"
    const val pageNewForm = "newForm"
    const val pageForms = "forms"
    const val pageWorkflows = "workflows"
    const val pageClients = "clients"
    const val pageCustomers = "customers"
    const val pageReports = "reports"
    const val pageApiKeys = "apiKeys"
    /**
     * The Reports page's own route parameters (issues #1007, #1036): the report that is open, how it is shown, and
     * the view that shows a report's history. Here, beside the page id, because a simulation's start page is built
     * on the server and the page reads it in the browser: one name each, so a rename cannot part them.
     */
    const val reportParam = "rpt"
    const val reportViewParam = "view"
    const val reportViewHistory = "history"

    /** The Reports page's `view` for making a report from simple choices (issue #1492), with no report open. */
    const val reportViewNew = "new"
    const val pageSimulations = "simulations"
    /** Become any user through the `becomeUser` fixture's form (issue #1183), for a caller with test access. */
    const val pageBecomeUser = "becomeUser"

    /** Joining a demo client from `public` (issue #1248): its roles, the shared-space disclosure, and the join. */
    const val pageJoin = "join"
    const val pageProfile = "profile"
    const val pageLogin = "login"
    const val pageRegister = "register"
    /** The invitation page (issue #751): `/wa/invite#token=...`, reached from a mailed link, never from the menu. */
    const val pageInvite = "invite"
    /** The fragment parameter the invitation page reads its token from; the backend builds the link with it. */
    const val inviteTokenParam = "token"
    /** The claim page (issue #751): the auth flow's third mode, for an account created for you, reached from the login page. */
    const val pageClaim = "claim"
    const val pageDebug = "debug"
}

/** Frontend functions a UiBlock may call, declared so both sides share the vocabulary (issue #483). */
object HACT {
    /**
     * Signing out: a request plus a redirect, which is why it cannot be a link and has to be a call.
     *
     * Takes no parameters, and the count is declared so the backend can refuse a UiBlock that passes some --
     * see [UiActionDef].
     */
    val logout = UiActionDef("logout")

    /**
     * Turn this session's debug behaviors on or off (issue #517). One parameter, the boolean as a string
     * (`"true"`/`"false"`): the "Enable debug" item passes `true` and "Turn off debug" passes `false`. A call
     * rather than a route because it changes session state via the env-auth endpoint and re-reads the config,
     * exactly as [logout] does its own side effect.
     */
    val setEnvDebug = UiActionDef("setEnvDebug", arity = 1)

    /**
     * Signing out of an **environment** (issue #486): the edge's counterpart to [logout], contributed to the
     * menu by `EdgeComponent` as an overlay. A request that clears the env-auth cookie, then a navigation to
     * the environment's sign-in page -- a call for the same reason [logout] is, not a route.
     *
     * **Two parameters, both URLs, and that is deliberate.** The frontend is served *by* an edge but knows
     * nothing about being behind one -- not which endpoint clears the perimeter cookie, nor where an edge's
     * sign-in page lives. Both are the edge's to know, so the edge supplies them as the call's arguments and
     * the function stays pure mechanism: fetch the first, navigate to the second. The order is
     * `(logoutPath, landingUrl)`:
     *
     *  - **`logoutPath`** -- the api-relative path of the clear-cookie endpoint (`EAEP.logout`), so the
     *    frontend's own api root is prepended exactly as for every other call, honoring a custom root.
     *  - **`landingUrl`** -- the absolute path to send the browser to afterward: the edge's landing page
     *    (issue #493), the anonymous surface a signed-out caller lands back on.
     *
     * Declared here beside [logout] because this object *is* the shared vocabulary of callable functions, not
     * a home-page-specific list; the edge is where it is contributed and implemented, but neither side of the
     * boundary can check the other, so the name has to live in the kernel. See `UiActions`.
     */
    val envLogout = UiActionDef("envLogout", arity = 2)

    /**
     * Navigate the whole window to a **same-origin path** (issue #493): a full page load out of this
     * single-page app, as opposed to a `UiRoute`, which routes *within* it.
     *
     * It exists because some destinations are server paths rather than in-app pages -- an edge's sign-in
     * page, the application reached through an edge -- and a `UiRoute` cannot express them: it names a
     * page, which the in-app router turns into its own path. The one parameter is the path, supplied by whoever
     * contributes the item (the edge knows its own roots); the implementation **guards** it to a same-origin
     * path, so a client-contributed overlay cannot turn a menu item into an off-site redirect. That guard is
     * why this is one named function rather than a bare "navigate anywhere": the operation is fixed and
     * reviewed, only the destination is data -- the same shape as `UiRoute` varying its page id.
     *
     * A frontend that renders one of these should mark it as leaving the app (issue #493) -- the ↗ affordance
     * is derived from *this action kind*, not from a field on the item, since leaving the app is inherent to
     * what `openPath` does.
     */
    val openPath = UiActionDef("openPath", arity = 1)

    /**
     * Become the **deployment admin** -- `admin` plus `allClients` -- in one click (issue #1183): the `becomeUser`
     * fixture as [com.dynamicruntime.common.test.TEP.deploymentAdminEmail], created on the first click and signed
     * in as on every later one, then home. A call rather than a route, since it changes the session as [logout] does.
     * Offered only to a caller with test access, for whom the fixture exists.
     */
    val becomeDeploymentAdmin = UiActionDef("becomeDeploymentAdmin")
}

/** Markdown fragment file ids for the home widget-group (each also the group's fragment namespace). */
@Suppress("ConstPropertyName")
object HFRAG {
    const val home = "home"

    /** The home file's namespace for the home page's own copy: its wordmark, title, intro, and note. */
    const val homeNs = "home"

    /** The home page's copy keys a self-serve create sets (issue #1336). */
    const val brand = "brand"
    const val title = "title"
    const val intro = "intro"
    const val note = "note"

    /** The home file's namespace for the forms listing's copy (issue #791). */
    const val formsNs = "forms"

    /**
     * The home file's namespace for the words of every "What is this?" link (issue #1330): [whatIsThisLink], and
     * [whatIsThisHide] for closing what one opened. The app's words rather than a page's, so the shell's copy.
     */
    const val whatIsThisNs = "whatIsThis"
    const val whatIsThisLink = "link"
    const val whatIsThisHide = "hide"

    /**
     * What a forms-listing row with no workflows shows in the workflow column (issue #791), in [formsNs]: Markdown,
     * so a client may say "Coming Soon…", link to the workflow pages, or say "No workflows available."
     */
    const val noWorkflows = "noWorkflows"

    /**
     * The key of a "What is this?" explanation (issue #1337), in the namespace of the copy it explains -- in this
     * file and in a page's own ([PFRAG]), as the profile file's `users.whatIsThis` is. Empty, the link is not offered.
     */
    const val explanation = "whatIsThis"

    /** The home file's namespace for the sandbox chip's explanation (issue #1337). */
    const val sandboxNs = "sandbox"

    /** The home file's namespace for a workflow's forms listing (issue #1337), beside [formsNs]'s forms listing. */
    const val workflowFormsNs = "workflowForms"
}

/**
 * The fragment files of the pages whose copy is not a widget-group's own (issue #1337): the Clients page, Reports, and
 * Design View. They hold the explanations those pages offer and are the place for the rest of their copy. Having no
 * UI-config of their own, they are named by the shell's ([HFRAG.home]'s config lists them after its own file), so a
 * page reads its file with no request of its own.
 */
@Suppress("ConstPropertyName")
object PFRAG {
    const val clients = "clients"
    const val reports = "reports"
    const val design = "design"

    /** Every page file, in the order the shell's config names them. */
    val files: List<String> = listOf(clients, reports, design)

    /** The Clients page's namespaces: its Sandbox, Stored configuration, and Files sections. */
    const val sandboxNs = "sandbox"
    const val storedConfigNs = "storedConfig"
    const val filesNs = "files"

    /**
     * The Clients page's namespaces for its other panels (issue #1397): the definition's four groups (About, How it
     * behaves, Platform, Where it runs), Forms and data, Copy, Images, and Menu.
     */
    const val aboutNs = "about"
    const val behaviorNs = "behavior"
    const val platformNs = "platform"
    const val runsNs = "runs"
    const val formsDataNs = "formsData"
    const val copyNs = "copy"
    const val imagesNs = "images"
    const val menuNs = "menu"

    /** The Clients page's namespace for recalculating a client's form states (issue #1412), within Forms and data. */
    const val formStatesNs = "formStates"

    /** The Clients page's namespace for a client's listing columns (issue #1491), within Forms and data. */
    const val listingColumnsNs = "listingColumns"

    /** The bundle editor page's namespaces (issue #1436): its Publishing section, and its list of bundles. */
    const val bundlePublishingNs = "bundlePublishing"
    const val bundleListNs = "bundleList"

    /** Every namespace of the Clients page whose `whatIsThis` explains a panel or a group of the page. */
    val clientsExplained: List<String> = listOf(
        aboutNs, behaviorNs, platformNs, runsNs, formsDataNs, listingColumnsNs, formStatesNs, copyNs, imagesNs, filesNs, sandboxNs,
        storedConfigNs, menuNs, bundlePublishingNs, bundleListNs,
    )

    /**
     * The Clients page's namespace for its Workflows section (issue #1384), whose [newWorkflowNote] the "Make a workflow
     * from a template" form shows its maker: how a workflow made restricted is opened to people. Empty as shipped, so
     * only a deployment that says it -- the `reference` flavor -- shows anything.
     */
    const val workflowsNs = "workflows"
    const val newWorkflowNote = "newWorkflowNote"

    /** The Reports page's namespace for its views: Per form, Grouped, and History. */
    const val viewsNs = "views"

    /** The Reports page's namespace for making a report from simple choices (issue #1492). */
    const val newReportNs = "newReport"

    /** Design View's namespace for a workflow's own copy against the shared definition's. */
    const val ownCopyNs = "ownCopy"
}

/**
 * Markdown *document* ids the home page links to (served whole, rendered as a page). The README is the entry
 * point; the rest are the repo docs it links to, registered so those interior links resolve to an in-app
 * document rather than the source repository (issue #492). Each id is also the `md-docs/<id>.md` resource name,
 * except for a generated document such as [schemaDialect], whose text is made at run time (issue #1347).
 */
@Suppress("ConstPropertyName")
object HDOC {
    /** The documentation's front door (issue #1510): what to read, by audience. Listed first. */
    const val startHere = "start-here"
    const val readme = "readme"
    const val extending = "extending"
    const val codeGuide = "code-guide"
    const val clientDefinition = "client-definition"
    const val deferredWork = "deferred-work"
    const val gedraConfigAndData = "gedra-config-and-data"
    const val gedraEntry = "gedra-entry"
    const val gedraStates = "gedra-states"
    const val userSearch = "user-search"
    const val gedraWorkflow = "gedra-workflow"
    const val uiBlock = "ui-block"
    const val reporting = "reporting"
    const val fileRefs = "file-refs"
    const val sandboxes = "sandboxes"
    const val schemaDialect = "schema-dialect"
    const val clientConfiguration = "client-configuration"
    const val apiKeysAndTokens = "api-keys-and-tokens"
    /** The guide for a program or an AI assistant using a site's API (issue #1493), which the endpoint catalog names. */
    const val apiGuide = "api-guide"
    /**
     * The app id in a document's address handed to a program (issue #1493): `/st/api/doc/api-guide`. An app id is
     * opaque and unread, so any would do; one fixed word gives every address the guide names the same shape.
     */
    const val apiAppId = "api"
    const val apiKeys = "api-keys"
    const val license = "license"
}
